// Helpers that turn a /stats/* response into chart-ready series.
// Works for both shapes the API returns:
//   individual: countByDate["2026-10-24"] = 3
//   team/manager: countByDate["username"]["2026-10-24"] = 3
// (sales gap: the leaf is { targetAmount, actualSalesAmount, ... } instead of a number)

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

const pad = (n) => String(n).padStart(2, "0");
export const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// One entry per local day, inclusive
export function dateList(start, end) {
  if (!start) return [];
  const out = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date((end || start).getFullYear(), (end || start).getMonth(), (end || start).getDate());
  while (cur <= last && out.length < 1000) {
    out.push(ymd(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

const isGrouped = (countByDate) => {
  const keys = Object.keys(countByDate || {});
  return keys.length > 0 && !DATE_KEY.test(keys[0]);
};

// Value of one metric on one date (summed over members when grouped)
export function valueOn(countByDate, date, pickLeaf = (v) => Number(v) || 0) {
  if (!countByDate) return 0;
  if (!isGrouped(countByDate)) return countByDate[date] !== undefined ? pickLeaf(countByDate[date]) : 0;
  let sum = 0;
  for (const user of Object.keys(countByDate)) {
    const v = countByDate[user]?.[date];
    if (v !== undefined) sum += pickLeaf(v);
  }
  return sum;
}

// Per-member totals across the given dates (grouped shape only)
export function totalsByMember(countByDate, dates, pickLeaf = (v) => Number(v) || 0) {
  if (!isGrouped(countByDate)) return {};
  const out = {};
  for (const user of Object.keys(countByDate)) {
    out[user] = dates.reduce((t, d) => t + (countByDate[user]?.[d] !== undefined ? pickLeaf(countByDate[user][d]) : 0), 0);
  }
  return out;
}

// Group days into day / week (Mon-start) / month buckets
export function bucketDates(dates, grain) {
  if (grain === "day") return dates.map((d) => ({ key: d, label: shortDay(d), dates: [d] }));
  const map = new Map();
  for (const d of dates) {
    const dt = new Date(`${d}T00:00:00`);
    let key, label;
    if (grain === "week") {
      const monday = new Date(dt);
      monday.setDate(dt.getDate() - ((dt.getDay() + 6) % 7));
      key = ymd(monday);
      label = `Wk ${shortDay(key)}`;
    } else {
      key = d.slice(0, 7);
      label = dt.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
    }
    if (!map.has(key)) map.set(key, { key, label, dates: [] });
    map.get(key).dates.push(d);
  }
  return [...map.values()];
}

export const autoGrain = (days) => (days <= 31 ? "day" : days <= 120 ? "week" : "month");

export function shortDay(d) {
  const dt = new Date(`${d}T00:00:00`);
  return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

// Activity metrics, in a FIXED order so each metric always keeps the same colour
// (validated categorical order; Rejected is dashed as a second cue next to Closed)
export const ACTIVITY_METRICS = [
  { key: "leads", label: "Leads Added", color: "#2a78d6", get: (s) => s?.totalLeadsAdded?.countByDate },
  { key: "presented", label: "Presented", color: "#eb6834", get: (s) => s?.totalPresentations?.countByDate },
  { key: "fuDone", label: "Follow-ups Done", color: "#1baf7a", get: (s) => s?.followUpScheduled?.countByDate },
  { key: "booking", label: "Booking", color: "#eda100", get: (s) => s?.finalStatus?.Booking?.countByDate },
  { key: "repeat", label: "Repeat Sales", color: "#e87ba4", get: (s) => s?.finalStatus?.Repeat?.countByDate },
  { key: "closed", label: "Closed", color: "#008300", get: (s) => s?.finalStatus?.Closed?.countByDate },
  { key: "rejected", label: "Rejected", color: "#e34948", dashed: true, get: (s) => s?.finalStatus?.Rejected?.countByDate },
];

export const metricTotal = (summary, metric) => {
  const map = {
    leads: summary?.totalLeadsAdded?.total,
    presented: summary?.totalPresentations?.total,
    fuDone: summary?.followUpScheduled?.total,
    booking: summary?.finalStatus?.Booking?.total,
    repeat: summary?.finalStatus?.Repeat?.total,
    closed: summary?.finalStatus?.Closed?.total,
    rejected: summary?.finalStatus?.Rejected?.total,
  };
  return Number(map[metric.key]) || 0;
};

export const salesLeaf = {
  actual: (v) => Number(v?.actualSalesAmount) || 0,
  target: (v) => Number(v?.targetAmount) || 0,
};

export const money = (n) =>
  `RM ${Number(n || 0).toLocaleString("en-MY", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// Quick date ranges (local time)
export function presetRange(key) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = (n) => { const d = new Date(today); d.setDate(d.getDate() + n); return d; };
  switch (key) {
    case "today": return [today, today];
    case "yesterday": return [days(-1), days(-1)];
    case "7d": return [days(-6), today];
    case "30d": return [days(-29), today];
    case "thisMonth": return [new Date(today.getFullYear(), today.getMonth(), 1), today];
    case "lastMonth": return [new Date(today.getFullYear(), today.getMonth() - 1, 1), new Date(today.getFullYear(), today.getMonth(), 0)];
    default: return [today, today];
  }
}

export const PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "thisMonth", label: "This month" },
  { key: "lastMonth", label: "Last month" },
];
